import { useMemo, useEffect, useState } from 'react'
import React from 'react'
import PropTypes from 'prop-types'
import {
  Button,
  MenuItem,
  TextField as MuiTextField,
  FormControlLabel,
  Checkbox,
  Typography,
} from '@mui/material'
import shadows from '@mui/material/styles/shadows'
import Stack from '@mui/material/Stack'
import { Field, Form, Formik } from 'formik'
import { TextField } from 'formik-mui'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { format } from 'date-fns'
import { bffService } from '../../../../../../services/utils/axios'
import ErrorFeedback from '../../../../../../components/DataTable/Feedback'
import {
  CheckboxControlContainer,
  CheckboxFieldContainer,
  StyledCheckboxCard,
} from '../../../styles'
import { getDateOnly } from '../../../components/formatDate'

/**
 * ProducerPlantDetails Form Component
 *
 * This component renders a form for producers to enter and edit their power plant details.
 * It captures critical information about the energy production facility including:
 * - Plant identification (name, type)
 * - Plant location details
 * - Technical specifications (installed power, commissioning date)
 * - Grid connection information (market location, meter number)
 * - Regulatory information (plant key, grid gridOperator)
 * - Delivery timing preferences
 *
 * The component includes validation for all required fields and handles postal code verification
 * through an API call. It also manages the start of delivery date and provides an option
 * for the next possible date.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.payload - The existing plant data to pre-populate the form
 * @param {Function} props.setPayload - Function to update the parent component's payload state
 * @param {Function} props.confirmPayload - Function called when form is successfully submitted
 * @param {Function} props.handleClose - Function to close the form dialog
 * @param {number} props.submit - Trigger for form submission (incremented to trigger)
 * @param {Function} props.setSubmit - Function to reset the submit counter
 * @param {Function} props.setCanNext - Function to enable/disable the next button based on form validity
 *
 * @example
 * <ProducerPlantDetails
 *   payload={plantData}
 *   setPayload={handleSetPayload}
 *   confirmPayload={handleNext}
 *   handleClose={onClose}
 *   submit={submitValue}
 *   setSubmit={setSubmitValue}
 *   setCanNext={setCanNextValue}
 * />
 *
 * @returns {React.ReactElement} A form for entering power plant details for producers
 */
function ProducerPlantDetails({
  payload,
  setPayload,
  confirmPayload,
  submit,
  setSubmit,
  setCanNext,
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [showError, setShowError] = useState(false)

  useEffect(() => {
    if (submit > 0) {
      document.getElementById('subTwo').click()
    }
    setSubmit(0)
  }, [submit, setSubmit])
  const closeFeedback = () => {
    setShowError(false)
  }
  const handlePostalCodeChange = async (e, setFieldValue) => {
    const newPostalCode = e.target.value
    setFieldValue('postalCode', newPostalCode)

    if (newPostalCode.length === 5) {
      try {
        const { data } = await bffService.get('/valid-addresses')
        if (data && Array.isArray(data)) {
          // Filter addresses by postal code
          const matchingAddresses = data.filter(
            (address) => address.postalCode === newPostalCode
          )

          if (matchingAddresses.length > 0) {
            // Use first matching address for city
            const city = matchingAddresses[0].city
            setFieldValue('city', city)
          } else {
            setFieldValue('postalCode', '')
            setFieldValue('city', '')
            setShowError(true)
          }
        } else {
          setFieldValue('postalCode', '')
          setFieldValue('city', '')
          setShowError(true)
        }
      } catch (error) {
        setFieldValue('city', '')
        setFieldValue('postalCode', '')
        setFieldValue('streetConsumer', [null])
        setShowError(true)
      }
    }
  }

  const initialValues = {
    plantName: payload?.plantName || '',
    energyPlantType: payload?.energyPlantType || '',
    addressOwner: payload?.plantAddress?.name || '',
    city: payload?.plantAddress?.city || '',
    postalCode: payload?.plantAddress?.postalCode,
    installedPowerKw: payload?.installedPowerKw || 0.0,
    commissioningDate: getDateOnly(payload?.commissioningDate) || null,
    marketLocation: payload?.marketLocation || '',
    meterNumber: payload?.meterNumber || '',
    plantKey: payload?.plantKey || '',
    gridOperator: payload?.gridOperator || '',
    isAlreadyClientOfUtility: payload?.isAlreadyClientOfUtility,
    utilityContractNumber: payload?.utilityContractNumber || '',
    isPlantTermsAndConditionsAccepted:
      payload?.isPlantTermsAndConditionsAccepted || false,
    startOfDeliveryDate: payload?.startOfDeliveryDate || null,
    isStartOfDeliveryNextPossibleDate:
      payload?.isStartOfDeliveryNextPossibleDate || false,
    isPlantAddressSameAsAddress: payload?.isPlantAddressSameAsAddress || false,
    plantAddress: payload?.plantAddress || {},
  }

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        plantName: Yup.string()
          .required(t('LBLCompulsory'))
          .label(t('LBLPlantName')),
        energyPlantType: Yup.string().required(t('LBLplantTypeReq')),
        addressOwner: Yup.string()
          .label(t('LBLBillingAddressOwner'))
          .when('isPlantAddressSameAsAddress', {
            is: false,
            then: (schema) => schema.required(t('LBLCompulsory')),
            otherwise: (schema) => schema.optional(),
          }),
        city: Yup.string().when('isPlantAddressSameAsAddress', {
          is: false,
          then: (schema) => schema.required(t('LBLCityReq')).label(t('city')),
          otherwise: (schema) => schema.optional(),
        }),
        postalCode: Yup.number().when('isPlantAddressSameAsAddress', {
          is: false,
          then: (schema) =>
            schema.required(t('LBLPostalCodeError')).label(t('PostalCode')),
          otherwise: (schema) => schema.optional(),
        }),
        installedPowerKw: Yup.string()
          .required(t('LBLinstalledPowerKwReq'))
          .label(t('LBLInstalledOutput')),
        commissioningDate: Yup.date()
          .nullable()
          .required(t('LBLcommissioningDateReq'))
          .label(t('LBLCommissioningDate')),
        startOfDeliveryDate: Yup.date()
          .nullable()
          .label(t('LBLStartOfDeliveryDate'))
          .when(
            'isStartOfDeliveryNextPossibleDate',
            ([isStartOfDeliveryNextPossibleDate]) => {
              if (!isStartOfDeliveryNextPossibleDate) {
                return Yup.date().required(t('LBLStartOfDeliveryDateReq'))
              }
            }
          ),
        marketLocation: Yup.string()
          .required(t('LBLmarketLocationReq'))
          .label(t('LBLMarketLocation')),
        meterNumber: Yup.string()
          .required(t('LBLmeterNumberReq'))
          .label(t('LBLMeterNumber')),
        plantKey: Yup.string()
          .required(t('LBLplantKeyReq'))
          .label(t('LBLPlantKey')),
        gridOperator: Yup.string()
          .required(t('LBLOperatorReq'))
          .label(t('LBLOperator')),
        isAlreadyClientOfUtility: Yup.bool().label('LBLStadtWerkeKunden'),
        utilityContractNumber: Yup.string()
          .when('isAlreadyClientOfUtility', {
            is: (isAlreadyClientOfUtility) => isAlreadyClientOfUtility,
            then: (schema) => schema.required(t('LBLCompulsory')),
            otherwise: (schema) => schema.optional(),
          })
          .label(t('LBLLastCustomerNum')),
        isStartOfDeliveryNextPossibleDate: Yup.bool().label(
          t('LBLNextPossibleDate')
        ),
        isPlantAddressSameAsAddress: Yup.bool().label(
          t('LBLPlantAddressSameAsAddress')
        ),
      }),
    [t]
  )

  /** @type {import("formik").FormikConfig<typeof initialValues>['onSubmit']} */
  const handleSubmit = (values, actions) => {
    const payload = {
      plantDetails: {
        plantName: values.plantName,
        energyPlantType: values.energyPlantType,

        installedPowerKw: values.installedPowerKw,
        commissioningDate: format(
          new Date(values.commissioningDate),
          'yyyy-MM-dd'
        ),
        marketLocation: values.marketLocation,
        meterNumber: values.meterNumber,
        plantKey: values.plantKey,
        gridOperator: values.gridOperator,
        isPlantTermsAndConditionsAccepted:
          values.isPlantTermsAndConditionsAccepted || false,
      },
      plantAddress: !values.isPlantAddressSameAsAddress
        ? {
            name: values.addressOwner,
            city: values.city,
            postalCode: values.postalCode,
          }
        : undefined,
      startOfDeliveryDate: values.startOfDeliveryDate
        ? format(new Date(values.startOfDeliveryDate), 'yyyy-MM-dd')
        : null,
      isAlreadyClientOfUtility: values.isAlreadyClientOfUtility,
      utilityContractNumber: values.utilityContractNumber,
      isStartOfDeliveryNextPossibleDate:
        values.isStartOfDeliveryNextPossibleDate,
      isPlantAddressSameAsAddress: values.isPlantAddressSameAsAddress,
    }
    setPayload(payload)
    confirmPayload()
    actions.setSubmitting(false)
    actions.setTouched({}, false)
    actions.setErrors({})
    actions.setFieldError({})
    actions.setFieldTouched({}, false, false)
  }
  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount={true}
        onSubmit={handleSubmit}
        validateOnChange={true}
      >
        {({ errors, touched, values, setFieldValue, isValid }) => (
          <Form noValidate>
            <>
              {setCanNext(touched && isValid)}

              {/* Plant Basic Information */}
              <Typography
                variant="h6"
                sx={{
                  mt: 3,
                  mb: 4,
                  fontWeight: 600,
                  color: 'text.primary',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  pb: 1,
                }}
              >
                {t('LBLPlantBasicInformation')}
              </Typography>
              <Stack my={5} direction="row" alignItems="center" spacing={8}>
                <Field
                  required
                  fullWidth
                  margin="none"
                  component={TextField}
                  id="plantName"
                  name="plantName"
                  label={t('LBLPlantName')}
                  InputProps={{
                    title: 'Bitte füllen Sie dieses Feld aus',
                  }}
                />
                <MuiTextField
                  select
                  required
                  id="energyPlantType"
                  name="energyPlantType"
                  open={open}
                  fullWidth
                  margin="none"
                  label={t('LBLPlantType')}
                  onClose={() => setOpen(false)}
                  onOpen={() => setOpen(true)}
                  defaultValue={payload?.energyPlantType}
                  value={values.energyPlantType}
                  onChange={(event) =>
                    setFieldValue('energyPlantType', event.target.value)
                  }
                >
                  <MenuItem value="HYDRO">{t('LBLHydroPower')}</MenuItem>
                  <MenuItem value="SOLAR">{t('LBLSolarPower')}</MenuItem>
                  <MenuItem value="BIOMASS">{t('LBLBioMass')}</MenuItem>
                  <MenuItem value="WIND">{t('LBLWindEnergy')}</MenuItem>
                </MuiTextField>
              </Stack>

              {/* Plant Technical Specifications */}
              <Typography
                variant="h6"
                sx={{
                  mt: 4,
                  mb: 4,
                  fontWeight: 600,
                  color: 'text.primary',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  pb: 1,
                }}
              >
                {t('LBLPlantTechnicalSpecifications')}
              </Typography>
              <Stack mb={5} direction="row" spacing={8}>
                <Field
                  margin="none"
                  type="number"
                  component={TextField}
                  id="installedPowerKW"
                  name="installedPowerKw"
                  label={t('LBLInstalledOutput')}
                  required
                  InputProps={{
                    title: 'Bitte füllen Sie dieses Feld aus',
                  }}
                  sx={{ flex: 1 }}
                />
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="de"
                >
                  <DatePicker
                    label={t('LBLCommissioningDate')}
                    PaperProps={{
                      sx: {
                        boxShadow: shadows[3],
                      },
                    }}
                    value={values.commissioningDate}
                    id={'commissioningDate'}
                    onChange={(newValue) => {
                      setFieldValue(
                        'commissioningDate',
                        newValue ? newValue.toDate() : null
                      )
                    }}
                    renderInput={(params) => (
                      <Field
                        component={TextField}
                        {...params}
                        required
                        name="commissioningDate"
                        margin="none"
                        autoComplete="off"
                        helperText={
                          touched.commissioningDate && errors.commissioningDate
                        }
                        error={
                          errors.commissioningDate && touched.commissioningDate
                        }
                        inputProps={{
                          ...params.inputProps,
                          placeholder: 'TT.MM.JJJJ',
                        }}
                        sx={{ flex: 1 }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Stack>

              {/* Grid Connection Information */}
              <Typography
                variant="h6"
                sx={{
                  mt: 4,
                  mb: 4,
                  fontWeight: 600,
                  color: 'text.primary',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  pb: 1,
                }}
              >
                {t('LBLGridConnectionInformation')}
              </Typography>
              <Stack mb={5} direction="row" spacing={8}>
                <Field
                  fullWidth
                  required
                  margin="none"
                  component={TextField}
                  id="marketLocation"
                  name="marketLocation"
                  label={t('LBLMarketLocation')}
                  InputProps={{
                    title: 'Bitte füllen Sie dieses Feld aus',
                  }}
                />
                <Field
                  fullWidth
                  required
                  margin="none"
                  component={TextField}
                  id="meterNumber"
                  name="meterNumber"
                  label={t('LBLMeterNumber')}
                  InputProps={{
                    title: 'Bitte füllen Sie dieses Feld aus',
                  }}
                />
              </Stack>

              <Stack mb={5} direction="row" spacing={8}>
                <Field
                  required
                  fullWidth
                  margin="none"
                  component={TextField}
                  id="plantKey"
                  name="plantKey"
                  label={t('LBLPlantKey')}
                  InputProps={{
                    title: 'Bitte füllen Sie dieses Feld aus',
                  }}
                />
                <Field
                  fullWidth
                  required
                  margin="none"
                  component={TextField}
                  id="gridOperator"
                  name="gridOperator"
                  label={t('LBLOperator')}
                  InputProps={{
                    title: 'Bitte füllen Sie dieses Feld aus',
                  }}
                />
              </Stack>

              {/* Plant Address Section */}
              <Typography
                variant="h6"
                sx={{
                  mt: 4,
                  mb: 4,
                  fontWeight: 600,
                  color: 'text.primary',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  pb: 1,
                }}
              >
                {t('LBLPlantAddressInformation')}
              </Typography>
              <Stack mb={5} direction="row" spacing={8}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="isPlantAddressSameAsAddress"
                      checked={values.isPlantAddressSameAsAddress}
                      onChange={(event) => {
                        setFieldValue(
                          'isPlantAddressSameAsAddress',
                          event.target.checked
                        )
                      }}
                    />
                  }
                  label={t('LBLPlantAddressSameAsAddress')}
                />
              </Stack>

              {!values.isPlantAddressSameAsAddress && (
                <>
                  <Stack mb={5} direction="row" spacing={8}>
                    <Field
                      fullWidth
                      margin="none"
                      component={TextField}
                      id="addressOwner"
                      name="addressOwner"
                      label={t('LBLAddressOwner')}
                      InputProps={{
                        title: 'Bitte füllen Sie dieses Feld aus',
                      }}
                      required
                    />
                    <Field
                      fullWidth
                      required
                      margin="none"
                      component={TextField}
                      id="postalCode"
                      name="postalCode"
                      label={t('PostalCode')}
                      value={values.postalCode}
                      InputProps={{
                        title: 'Bitte füllen Sie dieses Feld aus',
                      }}
                      onChange={(e) => handlePostalCodeChange(e, setFieldValue)}
                    />
                  </Stack>
                  <Stack mb={5} direction="row" spacing={8}>
                    <Field
                      required
                      margin="none"
                      component={TextField}
                      id="city"
                      name="city"
                      label={t('city')}
                      value={values.city}
                      InputProps={{
                        title: 'Bitte füllen Sie dieses Feld aus',
                      }}
                      sx={{ flex: 1 }}
                    />
                    <div style={{ flex: 1 }} />
                  </Stack>
                </>
              )}

              {/* Delivery Settings */}
              <Typography
                variant="h6"
                sx={{
                  mt: 4,
                  mb: 4,
                  fontWeight: 600,
                  color: 'text.primary',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  pb: 1,
                }}
              >
                {t('LBLDeliverySettings')}
              </Typography>

              <Stack mb={5} direction="row" spacing={8}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="isStartOfDeliveryNextPossibleDate"
                      checked={values.isStartOfDeliveryNextPossibleDate}
                      onChange={(event) => {
                        setFieldValue(
                          'isStartOfDeliveryNextPossibleDate',
                          event.target.checked
                        )
                      }}
                    />
                  }
                  label={t('LBLNextPossibleDate')}
                />
              </Stack>
              <Stack mb={1} direction="row" spacing={8}>
                {!values.isStartOfDeliveryNextPossibleDate ? (
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="de"
                  >
                    <DatePicker
                      label={t('LBLStartOfDeliveryDate')}
                      //minDate={dayjs(minStartDate)}
                      PaperProps={{
                        sx: {
                          boxShadow: shadows[3],
                        },
                      }}
                      value={values.startOfDeliveryDate}
                      id={'startOfDeliveryDate'}
                      onChange={(newValue) => {
                        setFieldValue(
                          'startOfDeliveryDate',
                          newValue ? newValue.toDate() : null
                        )
                      }}
                      renderInput={(params) => (
                        <Field
                          fullWidth
                          component={TextField}
                          {...params}
                          required
                          name="startOfDeliveryDate"
                          margin="none"
                          autoComplete="off"
                          helperText={
                            touched.startOfDeliveryDate &&
                            errors.startOfDeliveryDate
                          }
                          error={
                            errors.startOfDeliveryDate &&
                            touched.startOfDeliveryDate
                          }
                          inputProps={{
                            ...params.inputProps,
                            placeholder: 'TT.MM.JJJJ',
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                ) : undefined}
              </Stack>

              <div style={{ flex: 1 }} />

              {/* Terms and Conditions */}
              <Typography
                variant="h6"
                sx={{
                  mt: 4,
                  mb: 4,
                  fontWeight: 600,
                  color: 'text.primary',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  pb: 1,
                }}
              >
                {t('LBLTermsAndConditions')}
              </Typography>
              <Stack mb={5} direction="row" spacing={8}>
                <FormControlLabel
                  control={
                    <CheckboxControlContainer>
                      <CheckboxFieldContainer>
                        <Field
                          component={Checkbox}
                          type="checkbox"
                          id="isPlantTermsAndConditionsAccepted"
                          name="isPlantTermsAndConditionsAccepted"
                          onChange={(event) => {
                            setFieldValue(
                              'isPlantTermsAndConditionsAccepted',
                              event.target.checked
                            )
                          }}
                          checked={values.isPlantTermsAndConditionsAccepted}
                        />
                      </CheckboxFieldContainer>
                      <StyledCheckboxCard variant="outline">
                        <Typography
                          sx={{ fontSize: 14 }}
                          color="text.secondary"
                          gutterBottom
                        >
                          {t('LBLPlantDetailsAcceptancePlant')}
                        </Typography>
                      </StyledCheckboxCard>
                    </CheckboxControlContainer>
                  }
                />
              </Stack>

              <Button
                sx={{ display: 'none' }}
                id="subTwo"
                type="submit"
              ></Button>
            </>
          </Form>
        )}
      </Formik>
      <ErrorFeedback
        open={showError}
        title={t('LBLErrorTitle')}
        severity="warning"
        message={
          <Typography variant="subtitle1" sx={{ width: 400, mx: 10 }}>
            {t('LBLAuwAreaError')}
          </Typography>
        }
        handleClose={closeFeedback}
      />
    </>
  )
}
ProducerPlantDetails.propTypes = {
  payload: PropTypes.objectOf(PropTypes.any),
  setPayload: PropTypes.func.isRequired,
  confirmPayload: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
}
export default ProducerPlantDetails
